import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import './ContactForm.css'

const ContactForm = () => {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    location: '',
    businessName: '',
    companyDescription: '',
    websiteSocial: '',
    tagline: '',
    servicePackage: '',
    addons: [] as string[],
    budget: '',
    visionBoardLink: '',
    comments: '',
    referralSource: '',
    emailAcknowledgment: false
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleAddonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target
    setFormData(prev => {
      if (checked) {
        return { ...prev, addons: [...prev.addons, value] }
      } else {
        return { ...prev, addons: prev.addons.filter(addon => addon !== value) }
      }
    })
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.checked
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // Prepare data for Supabase - mapping all form fields to database columns
      const submissionData = {
        // Personal Information
        name: formData.name.trim(),
        email: formData.email.trim(),
        location: formData.location.trim(),
        
        // Business Information
        business_name: formData.businessName.trim(),
        company_description: formData.companyDescription.trim(),
        website_social: formData.websiteSocial?.trim() || null,
        tagline: formData.tagline?.trim() || null,
        
        // Service Information
        service_package: formData.servicePackage,
        addons: formData.addons.length > 0 ? formData.addons : null, // Store as array or null
        budget: formData.budget,
        
        // Additional Information
        vision_board_link: formData.visionBoardLink?.trim() || null,
        comments: formData.comments?.trim() || null,
        referral_source: formData.referralSource,
        email_acknowledgment: formData.emailAcknowledgment
      }
      
      console.log('Submitting form data:', submissionData)

      // Insert into Supabase
      const { error } = await supabase
        .from('contact_form_submissions')
        .insert([submissionData])

      if (error) {
        throw error
      }

      // Success!
      alert('Thank you! We will contact you soon.')
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        location: '',
        businessName: '',
        companyDescription: '',
        websiteSocial: '',
        tagline: '',
        servicePackage: '',
        addons: [],
        budget: '',
        visionBoardLink: '',
        comments: '',
        referralSource: '',
        emailAcknowledgment: false
      })

      // Navigate to home
      navigate('/')
    } catch (error: any) {
      console.error('Error submitting form:', error)
      setSubmitError(error.message || 'Something went wrong. Please try again.')
      alert('There was an error submitting your form. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="contact-form-wrapper">
      {/* Header Section */}
      <header className="form-header">
        <div className="header-background">
          {/* Decorative Icons */}
          <img src="/images/icons BCS-21.svg" alt="French Horn" className="header-icon icon-horn" />
          <img src="/images/icons BCS-24.svg" alt="Glasses" className="header-icon icon-glasses" />
          <img src="/images/icons BCS-25.svg" alt="Speech Bubble" className="header-icon icon-speech" />
          <img src="/images/icons BCS-33.svg" alt="Computer Mouse" className="header-icon icon-mouse" />
          <img src="/images/icons BCS-35.svg" alt="Megaphone" className="header-icon icon-megaphone" />
          
          {/* Left Side Logo */}
          <img src="/images/sidelogo.png" alt="BOLD Branding" className="header-side-logo" />
          
          {/* Center Logo */}
          <img src="/images/logo.png" alt="BOLD! CREATIVE STUDIO" className="header-logo" />
        </div>
      </header>

      {/* Enquiry Form Section */}
      <div className="contact-form-page">
        <h1 className="enquiry-form-title">Enquiry Form</h1>

        <div className="intro-text-block">
          <p>Welcome to BOLD Creative Studio! Excited to see you're ready to create something extraordinary with us! ❤️</p>
          <p>At BOLD Creative Studio, we specialize in <span className="highlight">crafting fearless, vibrant designs</span> that make your brand.</p>
          <p>To help us understand your vision and needs, fill out the form with as much detail as you can. The more we know, the bolder we can make your brand shine! ✨</p>
          <p>We'll get back to you within <strong>1-2 business days</strong> after you hit submit. (And hey, don't forget to peek into your junk/spam folder—just in case.)</p>
          <p>Let's turn up the volume on your brand and make some magic together! 🌸</p>
        </div>
        
        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Your Full name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Your E-mail *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="location">Where are you based? *</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="businessName">Your Business Name *</label>
            <input
              type="text"
              id="businessName"
              name="businessName"
              value={formData.businessName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="companyDescription">What does your company do? What services or products do you sell? Please give as many details as possible. *</label>
            <textarea
              id="companyDescription"
              name="companyDescription"
              value={formData.companyDescription}
              onChange={handleChange}
              required
              rows={5}
            />
          </div>

          <div className="form-group">
            <label htmlFor="websiteSocial">Share your brand's website and social networks (if applicable).</label>
            <textarea
              id="websiteSocial"
              name="websiteSocial"
              value={formData.websiteSocial}
              onChange={handleChange}
              rows={4}
            />
          </div>

          <div className="form-group">
            <label htmlFor="tagline">Business tagline or slogan (if applicable).</label>
            <textarea
              id="tagline"
              name="tagline"
              value={formData.tagline}
              onChange={handleChange}
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="service-packages-label">What services or packages are you looking for? (if you don't know any detail about the packages just choose any currently & we can discuss on call)</label>
            <p className="service-packages-note">*</p>
            
            <div className="service-packages">
              <div className="package-option">
                <input
                  type="radio"
                  id="package-kickstart"
                  name="servicePackage"
                  value="kickstart"
                  checked={formData.servicePackage === 'kickstart'}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="package-kickstart" className={`package-label ${formData.servicePackage === 'kickstart' ? 'package-selected' : ''}`}>
                  <span className="package-letter package-letter-a">A</span>
                  <span className="package-name">The Kickstart Pack</span>
                </label>
                <p className="package-description">
                  Perfect for <strong>small businesses & startups</strong> looking to build a strong foundation without stretching their budget. This pack provides the essentials to set your brand in motion with clarity and confidence.
                </p>
              </div>

              <div className="package-option">
                <input
                  type="radio"
                  id="package-essential"
                  name="servicePackage"
                  value="essential"
                  checked={formData.servicePackage === 'essential'}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="package-essential" className={`package-label ${formData.servicePackage === 'essential' ? 'package-selected' : ''}`}>
                  <span className="package-letter package-letter-b">B</span>
                  <span className="package-name">The Essential Pack</span>
                </label>
                <p className="package-description">
                  For <strong>growing brands</strong> wanting to <strong>stand out and disrupt the market</strong> with bold and impactful visuals. This pack strengthens your presence across platforms with added goodies to amplify your identity.
                </p>
              </div>

              <div className="package-option">
                <input
                  type="radio"
                  id="package-bold"
                  name="servicePackage"
                  value="bold"
                  checked={formData.servicePackage === 'bold'}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="package-bold" className={`package-label ${formData.servicePackage === 'bold' ? 'package-selected' : ''}`}>
                  <span className="package-letter package-letter-c">C</span>
                  <span className="package-name">The Bold & Beyond Pack</span>
                </label>
                <p className="package-description">
                  Ideal for <strong>established businesses</strong> wanting to <strong>dive deep into their brand strategy</strong> and invest in a comprehensive and cohesive identity. Perfect for brands ready to go big and lead their niche!
                </p>
              </div>

              <div className="package-option">
                <input
                  type="radio"
                  id="package-social"
                  name="servicePackage"
                  value="social"
                  checked={formData.servicePackage === 'social'}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="package-social" className={`package-label ${formData.servicePackage === 'social' ? 'package-selected' : ''}`}>
                  <span className="package-letter package-letter-d">D</span>
                  <span className="package-name">Social Media Management Packages</span>
                </label>
                <p className="package-description">
                  3 Different packages to suit your business growth & elevating your engagement & brand's presence.
                </p>
              </div>

              <div className="package-option">
                <input
                  type="radio"
                  id="package-custom"
                  name="servicePackage"
                  value="custom"
                  checked={formData.servicePackage === 'custom'}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="package-custom" className={`package-label ${formData.servicePackage === 'custom' ? 'package-selected' : ''}`}>
                  <span className="package-letter package-letter-e">E</span>
                  <span className="package-name">Customized Package OR Other Services</span>
                </label>
                <p className="package-description">
                  You can mix & match the services between the packages, or if you want some specific designing, you can checkbox here & specify the detailed artwork below.
                </p>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="addons-label">If you need any add-ons, please add them here. *</label>
            <div className="addons-list">
              <div className="addon-item">
                <input
                  type="checkbox"
                  id="addon-printable"
                  name="addon"
                  value="printable"
                  checked={formData.addons.includes('printable')}
                  onChange={handleAddonChange}
                />
                <label htmlFor="addon-printable">Printable assets (Business/Thank you/Loyalty/Gift card etc.)</label>
              </div>

              <div className="addon-item">
                <input
                  type="checkbox"
                  id="addon-shopping"
                  name="addon"
                  value="shopping"
                  checked={formData.addons.includes('shopping')}
                  onChange={handleAddonChange}
                />
                <label htmlFor="addon-shopping">Shopping/Paper Bag</label>
              </div>

              <div className="addon-item">
                <input
                  type="checkbox"
                  id="addon-menu"
                  name="addon"
                  value="menu"
                  checked={formData.addons.includes('menu')}
                  onChange={handleAddonChange}
                />
                <label htmlFor="addon-menu">Menu</label>
              </div>

              <div className="addon-item">
                <input
                  type="checkbox"
                  id="addon-price"
                  name="addon"
                  value="price"
                  checked={formData.addons.includes('price')}
                  onChange={handleAddonChange}
                />
                <label htmlFor="addon-price">Price List</label>
              </div>

              <div className="addon-item">
                <input
                  type="checkbox"
                  id="addon-website"
                  name="addon"
                  value="website"
                  checked={formData.addons.includes('website')}
                  onChange={handleAddonChange}
                />
                <label htmlFor="addon-website">Website Artworks</label>
              </div>

              <div className="addon-item">
                <input
                  type="checkbox"
                  id="addon-dvc"
                  name="addon"
                  value="dvc"
                  checked={formData.addons.includes('dvc')}
                  onChange={handleAddonChange}
                />
                <label htmlFor="addon-dvc">Concept & Storyboards of DVCs</label>
              </div>

              <div className="addon-item">
                <input
                  type="checkbox"
                  id="addon-pr"
                  name="addon"
                  value="pr"
                  checked={formData.addons.includes('pr')}
                  onChange={handleAddonChange}
                />
                <label htmlFor="addon-pr">PR Cards/PR Boxes Design</label>
              </div>

              <div className="addon-item">
                <input
                  type="checkbox"
                  id="addon-box"
                  name="addon"
                  value="box"
                  checked={formData.addons.includes('box')}
                  onChange={handleAddonChange}
                />
                <label htmlFor="addon-box">Box Design</label>
              </div>

              <div className="addon-item">
                <input
                  type="checkbox"
                  id="addon-labels"
                  name="addon"
                  value="labels"
                  checked={formData.addons.includes('labels')}
                  onChange={handleAddonChange}
                />
                <label htmlFor="addon-labels">Product Packaging Labels</label>
              </div>

              <div className="addon-item">
                <input
                  type="checkbox"
                  id="addon-flyers"
                  name="addon"
                  value="flyers"
                  checked={formData.addons.includes('flyers')}
                  onChange={handleAddonChange}
                />
                <label htmlFor="addon-flyers">Flyers/Posters/Brochures</label>
              </div>

              <div className="addon-item">
                <input
                  type="checkbox"
                  id="addon-others"
                  name="addon"
                  value="others"
                  checked={formData.addons.includes('others')}
                  onChange={handleAddonChange}
                />
                <label htmlFor="addon-others">Others</label>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="budget-label">What is your budget? *</label>
            <div className="budget-options">
              <div className="budget-option">
                <input
                  type="radio"
                  id="budget-under-500"
                  name="budget"
                  value="under-500"
                  checked={formData.budget === 'under-500'}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="budget-under-500">&lt;$500 (for only specific artwork designing e.g. cards, bags etc.)</label>
              </div>

              <div className="budget-option">
                <input
                  type="radio"
                  id="budget-500-1500"
                  name="budget"
                  value="500-1500"
                  checked={formData.budget === '500-1500'}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="budget-500-1500">$500-$1,500</label>
              </div>

              <div className="budget-option">
                <input
                  type="radio"
                  id="budget-1500-2500"
                  name="budget"
                  value="1500-2500"
                  checked={formData.budget === '1500-2500'}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="budget-1500-2500">$1,500-$2,500</label>
              </div>

              <div className="budget-option">
                <input
                  type="radio"
                  id="budget-2500-plus"
                  name="budget"
                  value="2500-plus"
                  checked={formData.budget === '2500-plus'}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="budget-2500-plus">$2,500+</label>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="visionBoardLink">Please attach a link to the Pinterest or any other vision board with all the brand assets and designs you like.</label>
            <p className="form-hint">Try not to skip this step if you have something in mind for your branding.</p>
            <input
              type="text"
              id="visionBoardLink"
              name="visionBoardLink"
              value={formData.visionBoardLink}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="comments">Any other comments and/or questions? Add below.</label>
            <textarea
              id="comments"
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              rows={5}
            />
          </div>

          <div className="form-group">
            <label htmlFor="referralSource">How did you hear about BOLD CS? *</label>
            <select
              id="referralSource"
              name="referralSource"
              value={formData.referralSource}
              onChange={handleChange}
              required
            >
              <option value="">Select an option</option>
              <option value="google">Google Search</option>
              <option value="social-media">Social Media</option>
              <option value="referral">Referral from friend/colleague</option>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="pinterest">Pinterest</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group email-instructions">
            <p className="email-instructions-text">If you haven't received an email reply in 72 hours, please check your spam folder or reach out to me through my Instagram DMs. *</p>
            <div className="acknowledgment-checkbox">
              <input
                type="checkbox"
                id="emailAcknowledgment"
                name="emailAcknowledgment"
                checked={formData.emailAcknowledgment}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="emailAcknowledgment">Of course!</label>
            </div>
          </div>

          <div className="form-footer">
            <p className="thank-you-message">Thank You for your time!</p>
            <p className="team-signature">Team BOLD! ✨</p>
          </div>

          {submitError && (
            <div className="submit-error">
              <p>{submitError}</p>
            </div>
          )}
          <button 
            type="submit" 
            className="submit-button" 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'SENDING...' : 'SEND →'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default ContactForm

